// Remote Data — browse the CloudShare field-data manifest maintained by the
// server-side discovery scanner (see services/ais-api/src/remote-storage.js),
// send folders to a project DAM, and manage portfolio auto-sync (automation
// that moves new GoPro/LAS files to the mapped project's DAM after each scan).

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function fmtBytes(b) {
  const n = Number(b) || 0;
  if (n >= 1073741824) return (n / 1073741824).toFixed(2) + " GB";
  if (n >= 1048576) return (n / 1048576).toFixed(1) + " MB";
  if (n >= 1024) return (n / 1024).toFixed(0) + " KB";
  return n + " B";
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

// Status precedence: still-uploading > local sync state. The manifest only ever
// holds files the last scan actually found on the FTP, so there's no "missing" state.
function fileBadge(f) {
  if (f.stable === false) return { label: "Uploading…", color: "#f59e0b" };
  if (f.syncState === "synced") return { label: "Synced", color: "#22c55e" };
  if (f.syncState === "size_mismatch") return { label: "Partial local", color: "#f59e0b" };
  return { label: "Cloud only", color: "#60a5fa" };
}

function badgeHtml({ label, color }) {
  return `<span class="small" style="color:${color}; white-space:nowrap;">&#9679; ${esc(label)}</span>`;
}

// Mirrors surfaces/index.js's SURFACE_BAND_STATS (and server.js's SURFACE_OUTPUT_TYPES) —
// duplicated here rather than imported, same as this file already duplicates the DAM
// category list rather than reaching into the surfaces tool module.
const RD_SURFACE_BAND_STATS = [
  { key: "min", label: "Min" },
  { key: "max", label: "Max" },
  { key: "mean", label: "Mean" },
  { key: "idw", label: "IDW" },
  { key: "count", label: "Count" },
  { key: "stdev", label: "Std Dev" },
];

function createRemoteDataTool(app) {
  let containerEl = null;
  let status = null;
  let statusError = "";
  let dir = "";
  let tree = { dirs: [], files: [] };
  let treeLoading = false;
  let query = "";
  let results = null; // { files, total } when searching
  let searchTimer = null;
  let pollTimer = null;
  // Send-to-DAM state: the picker form + the transfer being watched.
  let sendOpen = false;
  let sendProject = "";
  let sendCategory = "360-dock-photos";
  let projects = null; // [{code,name}] from the shared project catalog
  let transfer = null; // summary of the running/last transfer
  let transferTimer = null;
  // Automation state: the portfolio rule + enable-time preview flow.
  let auto = null; // { running, rules, lastRun } from /automation
  let autoPreview = null; // dry-run result while the enable confirmation is open
  let autoPreviewLoading = false;
  let autoConfirmOpen = false;
  let autoPollTimer = null;
  let autoBusy = false;
  let autoRuns = null; // recent pass history, loaded on demand
  let autoRunsOpen = false;
  let passSamples = []; // [{t, b}] processed-bytes samples for rate/ETA
  // Potree queue visibility: polled only while the panel is open, since it's
  // just a read (GET on this path is exempt from the job-creation rate limit).
  let potreeQueueOpen = false;
  let potreeQueueData = null; // { jobs, queue: {running, queued} } from /api/point-cloud/potree/jobs
  let potreeQueuePollTimer = null;
  // Deliverables mirror (DAM deliverables -> CloudShare <marina>/Deliverables/).
  let mirror = null; // { enabled, running, pass, lastRunAt, lastResult } from /mirror
  let mirrorPreview = null;
  let mirrorPreviewLoading = false;
  let mirrorConfirmOpen = false;
  let mirrorBusy = false;
  let mirrorPollTimer = null;

  const DAM_CATEGORIES = ["360-dock-photos", "las-files", "geotiff-files", "vector-files", "surfaces", "source-data", "project-files", "tilesets", "deliverables", "uploaded-from-client", "gnss-raw"];

  async function loadStatus() {
    try {
      const r = await fetch("/api/remote-storage/status");
      status = await r.json();
      statusError = status?.error || "";
    } catch (err) {
      status = null;
      statusError = String(err?.message || err);
    }
    render();
    // While a scan runs, keep polling and refresh the browser when it finishes.
    if (status?.scanning && !pollTimer) {
      pollTimer = setInterval(async () => {
        const wasScanning = status?.scanning;
        await loadStatus();
        if (wasScanning && status && !status.scanning) {
          clearInterval(pollTimer);
          pollTimer = null;
          loadTree();
        }
      }, 3000);
    }
  }

  async function loadTree() {
    treeLoading = true;
    render();
    try {
      const r = await fetch(`/api/remote-storage/tree?dir=${encodeURIComponent(dir)}`);
      tree = await r.json();
      if (tree.error) throw new Error(tree.error);
    } catch {
      tree = { dirs: [], files: [] };
    }
    treeLoading = false;
    render();
  }

  async function runSearch() {
    if (!query.trim()) { results = null; render(); return; }
    try {
      const r = await fetch(`/api/remote-storage/files?q=${encodeURIComponent(query.trim())}&limit=200`);
      results = await r.json();
    } catch {
      results = { files: [], total: 0 };
    }
    render();
  }

  async function startScan() {
    try {
      const r = await fetch("/api/remote-storage/scan", { method: "POST" });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "Scan failed to start.");
      app.ui.toast("Discovery scan started.");
      await loadStatus();
    } catch (err) {
      app.ui.toast(`Scan failed: ${err.message}`);
    }
  }

  function openDir(name) {
    dir = dir ? `${dir}/${name}` : name;
    sendOpen = false;
    loadTree();
  }

  async function loadProjects() {
    try {
      const r = await fetch("/api/projects/catalog?scope=portfolio");
      const data = await r.json();
      projects = (data.catalog?.projects || [])
        .filter((p) => p?.code)
        .map((p) => ({ code: p.code, name: p.name || p.code }))
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      projects = [];
    }
    render();
  }

  async function pollTransfer() {
    try {
      const r = await fetch("/api/remote-storage/transfers");
      const data = await r.json();
      const t = (data.transfers || []).find((x) => x.id === transfer?.id) || data.transfers?.[0] || null;
      if (t) transfer = t;
      if (transfer && transfer.status !== "running" && transferTimer) {
        clearInterval(transferTimer);
        transferTimer = null;
        app.ui.toast(transfer.status === "done"
          ? `Transfer complete — ${transfer.copied} copied, ${transfer.skipped} already in DAM${transfer.errorCount ? `, ${transfer.errorCount} failed` : ""}.`
          : `Transfer ${transfer.status}.`);
      }
      if (transfer?.status === "running" && !transferTimer) transferTimer = setInterval(pollTransfer, 2000);
    } catch {}
    render();
  }

  async function startTransfer() {
    if (!sendProject) { app.ui.toast("Pick a project first."); return; }
    try {
      const r = await fetch("/api/remote-storage/transfer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dir, marina: sendProject, category: sendCategory }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "transfer failed to start");
      transfer = data.transfer;
      sendOpen = false;
      if (!transferTimer) transferTimer = setInterval(pollTransfer, 2000);
      render();
    } catch (err) {
      app.ui.toast(`Transfer failed: ${err.message}`);
    }
  }

  async function cancelTransfer() {
    try { await fetch("/api/remote-storage/transfer/cancel", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: transfer?.id }) }); } catch {}
  }

  function autoRule() {
    return (auto?.rules || []).find((r) => r.scope === "client") || null;
  }

  function autoContent() {
    const c = autoRule()?.config?.content || {};
    return { gopro: c.gopro !== false, las: c.las !== false, trimble: c.trimble !== false };
  }

  function autoPotreeEnabled() {
    return autoRule()?.config?.autoPotree === true;
  }

  function autoOpusSubmitEnabled() {
    return autoRule()?.config?.autoOpusSubmit === true;
  }

  function autoSurfaceEnabled() {
    return autoRule()?.config?.autoSurface === true;
  }

  function autoSurfaceResolutionValue() {
    const r = autoRule()?.config?.autoSurfaceResolution;
    return Number.isFinite(Number(r)) ? Number(r) : "";
  }

  function autoSurfaceOutputTypesValue() {
    const t = autoRule()?.config?.autoSurfaceOutputTypes;
    return Array.isArray(t) && t.length ? t : ["mean"];
  }

  async function loadAutomation() {
    try {
      const r = await fetch("/api/remote-storage/automation");
      const data = await r.json();
      if (data.ok) auto = data;
    } catch {}
    // Sample processed bytes for the client-side rate/ETA estimate. Rate is
    // computed over a trailing window, so it self-corrects as transfers vary.
    if (auto?.running && auto.pass) {
      const processed = (auto.pass.bytesDone || 0) + (auto.pass.active?.bytesCopied || 0) + (auto.pass.active?.bytesInFlight || 0);
      passSamples.push({ t: Date.now(), b: processed });
      if (passSamples.length > 20) passSamples.shift();
    } else {
      passSamples = [];
    }
    render();
    // While an auto-sync pass runs, keep this section and the transfer box live.
    if (auto?.running && !autoPollTimer) {
      autoPollTimer = setInterval(async () => {
        const was = auto?.running;
        await Promise.all([loadAutomation(), pollTransfer()]);
        if (was && auto && !auto.running) {
          clearInterval(autoPollTimer);
          autoPollTimer = null;
          const res = autoRule()?.last_result;
          if (res) app.ui.toast(`Auto-sync ${res.status}: ${res.copied} copied, ${res.skipped} skipped${res.errors ? `, ${res.errors} errors` : ""}.`);
          if (autoRunsOpen) loadAutoRuns();
        }
      }, 3000);
    }
  }

  async function loadMirror() {
    try {
      const r = await fetch("/api/remote-storage/mirror");
      const data = await r.json();
      if (data.ok) mirror = data;
    } catch {}
    render();
    if (mirror?.running && !mirrorPollTimer) {
      mirrorPollTimer = setInterval(async () => {
        const was = mirror?.running;
        await loadMirror();
        if (was && mirror && !mirror.running) {
          clearInterval(mirrorPollTimer);
          mirrorPollTimer = null;
          const res = mirror.lastResult;
          if (res) app.ui.toast(`Deliverables mirror ${res.status}: ${res.uploaded} uploaded${res.errors ? `, ${res.errors} failed` : ""}.`);
        }
      }, 3000);
    }
  }

  async function openMirrorConfirm() {
    mirrorConfirmOpen = true;
    mirrorPreviewLoading = true;
    mirrorPreview = null;
    render();
    try {
      const r = await fetch("/api/remote-storage/mirror/preview", { method: "POST" });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "preview failed");
      mirrorPreview = data;
    } catch (err) {
      mirrorPreview = { error: String(err?.message || err) };
    }
    mirrorPreviewLoading = false;
    render();
  }

  async function setMirrorEnabled(enabled) {
    mirrorBusy = true;
    render();
    try {
      const r = await fetch("/api/remote-storage/mirror", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "failed to save");
      mirrorConfirmOpen = false;
      mirrorPreview = null;
      await loadMirror();
    } catch (err) {
      app.ui.toast(`Deliverables mirror: ${err.message}`);
    }
    mirrorBusy = false;
    render();
  }

  async function runMirrorNow() {
    try {
      const r = await fetch("/api/remote-storage/mirror/run", { method: "POST" });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "failed to start");
      app.ui.toast("Deliverables mirror pass started.");
      await loadMirror();
    } catch (err) {
      app.ui.toast(`Deliverables mirror: ${err.message}`);
    }
  }

  function mirrorUnmappedHtml(list) {
    if (!list?.length) return "";
    return `<div class="small" style="color:#f59e0b;">${list.length} project${list.length === 1 ? " has" : "s have"} deliverables but no CloudShare marina folder (skipped): ${list.slice(0, 12).map((u) => esc(u.code)).join(", ")}${list.length > 12 ? ", …" : ""}</div>`;
  }

  function mirrorHtml() {
    const enabled = !!mirror?.enabled;
    const res = mirror?.lastResult;
    let body = "";
    if (mirrorConfirmOpen) {
      const p = mirrorPreview;
      body = `
        <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,0.2); border-radius:6px; display:grid; gap:6px;">
          ${mirrorPreviewLoading ? `<div class="small" style="color:var(--muted);">Checking every project's deliverables…</div>` : ""}
          ${p?.error ? `<div class="small" style="color:#ef4444;">${esc(p.error)}</div>` : ""}
          ${p && !p.error ? `
            <div class="small">
              <b>${p.mappedProjects}</b> projects have a CloudShare marina folder. Enabling now uploads
              <b>${p.pendingFiles} file${p.pendingFiles === 1 ? "" : "s"} (${fmtBytes(p.pendingBytes)})</b>
              into each folder's <b>${esc(p.folder)}/</b>, then new or changed deliverables after every scan.
              Nothing on CloudShare is ever deleted.
            </div>
            ${mirrorUnmappedHtml(p.unmappedProjects)}
            ${p.missingSources?.length ? `<div class="small" style="color:#f59e0b;">${p.missingSources.length} deliverable entr${p.missingSources.length === 1 ? "y points" : "ies point"} at a file that no longer exists in S3 (skipped).</div>` : ""}
          ` : ""}
          <div class="row" style="gap:6px;">
            <button id="rd-mirror-enable" class="btn" ${mirrorBusy || mirrorPreviewLoading || !!p?.error ? "disabled" : ""}>Enable Mirror</button>
            <button id="rd-mirror-cancel" class="btn">Cancel</button>
          </div>
        </div>`;
    } else if (enabled) {
      const pass = mirror?.pass;
      let progress = "";
      if (pass) {
        const done = (pass.bytesDone || 0) + (pass.bytesInFlight || 0);
        const pct = pass.totalBytes ? Math.min(100, Math.round((done / pass.totalBytes) * 100)) : 0;
        progress = `
          <div class="small">${pass.waiting ? "Paused while 360 Automation or a transfer runs… " : ""}${pass.filesDone}/${pass.totalFiles} files · ${esc(fmtBytes(done))} of ${esc(fmtBytes(pass.totalBytes))}${pass.errorCount ? ` · <span style="color:#ef4444;">${pass.errorCount} failed</span>` : ""}</div>
          <div style="height:4px; border-radius:2px; background:rgba(148,163,184,0.2);"><div style="height:100%; width:${pct}%; border-radius:2px; background:var(--accent, #60a5fa);"></div></div>
          ${(pass.current || []).map((c) => `<div class="small" style="color:var(--muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${esc(c)}">↑ ${esc(c)}</div>`).join("")}`;
      }
      const lastLine = !mirror?.lastRunAt
        ? "No passes yet — runs after each scan."
        : `Last pass ${fmtWhen(mirror.lastRunAt)}${res ? ` — ${res.status}: ${res.uploaded} uploaded${res.replaced ? ` (${res.replaced} updated)` : ""}, ${res.upToDate} already up to date${res.errors ? `, ${res.errors} failed` : ""}` : ""}`;
      body = `
        <div style="margin-top:6px; display:grid; gap:6px;">
          <div class="small" style="color:var(--muted);">${esc(lastLine)}</div>
          ${progress}
          ${res?.errorDetail?.length && !pass ? `<div class="small" style="color:#ef4444;">${res.errorDetail.slice(0, 3).map((e) => `${esc(e.path)}: ${esc(e.error)}`).join("<br/>")}</div>` : ""}
          ${mirrorUnmappedHtml(res?.unmappedProjects)}
          <div class="row" style="gap:6px;">
            <button id="rd-mirror-run" class="btn small" ${mirror?.running || mirrorBusy ? "disabled" : ""}>Run Now</button>
          </div>
        </div>`;
    }
    return `
      <details class="toolset-group" open>
        <summary>Deliverables → CloudShare</summary>
        <div class="toolset-content">
          <label class="small" style="display:flex; gap:6px; align-items:center; cursor:pointer;">
            <input type="checkbox" id="rd-mirror-toggle" ${enabled || mirrorConfirmOpen ? "checked" : ""} ${mirrorBusy || mirror === null ? "disabled" : ""}/>
            <span><b>Mirror deliverables</b> — copy each Safe Harbor project's deliverables to its marina folder's <b>Deliverables/</b> on CloudShare (add and update only)</span>
          </label>
          ${body}
        </div>
      </details>`;
  }

  async function loadAutoRuns() {
    try {
      const r = await fetch("/api/remote-storage/automation/runs?limit=10");
      const data = await r.json();
      if (data.ok) autoRuns = data.runs;
    } catch {
      autoRuns = [];
    }
    render();
  }

  async function loadPotreeQueue() {
    try {
      const r = await fetch("/api/point-cloud/potree/jobs");
      const data = await r.json();
      if (data.ok) potreeQueueData = { jobs: data.jobs || [], queue: data.queue || { running: 0, queued: 0 } };
    } catch {
      potreeQueueData = potreeQueueData || { jobs: [], queue: { running: 0, queued: 0 } };
    }
    render();
  }

  function passRate() {
    if (passSamples.length < 2) return null;
    const first = passSamples[0];
    const last = passSamples[passSamples.length - 1];
    const dt = (last.t - first.t) / 1000;
    if (dt < 5 || last.b <= first.b) return null;
    return (last.b - first.b) / dt; // bytes/sec
  }

  async function saveAutoRule({ enabled, content, autoPotree, autoOpusSubmit, autoSurface, autoSurfaceResolution, autoSurfaceOutputTypes }) {
    autoBusy = true;
    render();
    try {
      const rule = autoRule();
      const r = await fetch("/api/remote-storage/automation/rule", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: rule?.client_id || "safeharbor",
          enabled: enabled ?? Boolean(rule?.enabled),
          content: content ?? autoContent(),
          excludedCodes: rule?.config?.excludedCodes || [],
          autoPotree: autoPotree ?? autoPotreeEnabled(),
          autoOpusSubmit: autoOpusSubmit ?? autoOpusSubmitEnabled(),
          autoSurface: autoSurface ?? autoSurfaceEnabled(),
          // autoSurfaceResolution can be a deliberate null (user cleared the field), so this
          // checks undefined specifically rather than using ?? (which would also catch null).
          autoSurfaceResolution: autoSurfaceResolution !== undefined ? autoSurfaceResolution : (autoSurfaceResolutionValue() || null),
          autoSurfaceOutputTypes: autoSurfaceOutputTypes ?? autoSurfaceOutputTypesValue(),
        }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "failed to save rule");
      autoConfirmOpen = false;
      autoPreview = null;
      await loadAutomation();
    } catch (err) {
      app.ui.toast(`Automation: ${err.message}`);
    }
    autoBusy = false;
    render();
  }

  async function openAutoConfirm() {
    autoConfirmOpen = true;
    autoPreviewLoading = true;
    autoPreview = null;
    render();
    try {
      const r = await fetch("/api/remote-storage/automation/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: autoRule()?.client_id || "safeharbor", content: autoContent() }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "preview failed");
      autoPreview = data;
    } catch (err) {
      autoPreview = { error: String(err?.message || err) };
    }
    autoPreviewLoading = false;
    render();
  }

  async function runAutoNow() {
    try {
      const r = await fetch("/api/remote-storage/automation/run", { method: "POST" });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "failed to start");
      app.ui.toast("Auto-sync pass started.");
      await loadAutomation();
      pollTransfer();
    } catch (err) {
      app.ui.toast(`Auto-sync: ${err.message}`);
    }
  }

  function passProgressHtml() {
    const pass = auto?.pass;
    if (!auto?.running) return "";
    if (!pass || !pass.totalJobs) return `<div class="small" style="color:#60a5fa;">Auto-sync pass running…</div>`;
    const processed = (pass.bytesDone || 0) + (pass.active?.bytesCopied || 0) + (pass.active?.bytesInFlight || 0);
    const pct = pass.totalBytes ? Math.min(100, Math.round((processed / pass.totalBytes) * 100)) : 0;
    const rate = passRate();
    const remaining = Math.max(0, pass.totalBytes - processed);
    const folderNo = Math.min(pass.doneJobs + 1, pass.totalJobs);
    const cur = pass.current;
    const active = pass.active;
    return `
      <div style="padding:6px 8px; border:1px solid rgba(148,163,184,0.2); border-radius:6px; display:grid; gap:4px;">
        <div class="small" style="display:flex; justify-content:space-between; gap:8px;">
          <span style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
            Auto-sync — folder ${folderNo}/${pass.totalJobs}${cur ? `: <b>${esc(cur.marina)}</b> / ${esc(cur.category)}` : ""}
          </span>
          <span style="white-space:nowrap; color:var(--muted);">${pct}%</span>
        </div>
        <div style="height:5px; border-radius:3px; background:rgba(148,163,184,0.2);"><div style="height:100%; width:${pct}%; border-radius:3px; background:var(--accent, #60a5fa);"></div></div>
        <div class="small" style="color:var(--muted);">
          ${esc(fmtBytes(processed))} of ${esc(fmtBytes(pass.totalBytes))}
          ${active?.status === "running" ? ` · ${active.done}/${active.total} files in folder` : ""}
          ${rate ? ` · ${esc(fmtBytes(rate))}/s · ~${esc(fmtDuration(remaining / rate))} left` : ""}
        </div>
      </div>`;
  }

  function autoRunsHtml() {
    const rows = (autoRuns || []).map((r) => {
      const dur = r.finished_at ? fmtDuration((new Date(r.finished_at) - new Date(r.started_at)) / 1000) : "…";
      const color = r.status === "ok" ? "var(--muted)" : "#f59e0b";
      return `<div class="small" style="display:flex; justify-content:space-between; gap:8px;">
        <span style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(fmtWhen(r.started_at))} · ${esc(r.run_trigger || "")}</span>
        <span style="white-space:nowrap; color:${color};">${esc(r.status)} — ${r.copied} copied, ${r.skipped} skipped${r.errors ? `, ${r.errors} errors` : ""} · ${esc(fmtBytes(r.bytes_copied))} · ${esc(dur)}</span>
      </div>`;
    }).join("");
    return `
      <details id="rd-auto-runs" ${autoRunsOpen ? "open" : ""}>
        <summary class="small" style="cursor:pointer; color:var(--muted);">Recent passes</summary>
        <div style="display:grid; gap:3px; padding:4px 0 0 2px; max-height:180px; overflow:auto;">
          ${autoRuns === null ? `<div class="small" style="color:var(--muted);">Loading…</div>` : rows || `<div class="small" style="color:var(--muted);">No passes recorded yet.</div>`}
        </div>
      </details>`;
  }

  function potreeJobRowHtml(j) {
    const color = j.status === "failed" ? "#ef4444" : j.status === "completed" ? "#22c55e" : j.status === "running" ? "#60a5fa" : "var(--muted)";
    const statusText = j.status === "running" ? `running ${j.progressPct ?? 0}%` : j.status;
    return `<div class="small" style="display:flex; justify-content:space-between; gap:8px;">
      <span style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(j.datasetName || j.sourceFileName || j.id)}${j.marina ? ` <span style="color:var(--muted);">(${esc(j.marina)})</span>` : ""}</span>
      <span style="white-space:nowrap; color:${color};">${esc(statusText)}</span>
    </div>`;
  }

  function potreeQueueHtml() {
    const q = potreeQueueData?.queue || { running: 0, queued: 0 };
    const jobs = (potreeQueueData?.jobs || [])
      .slice()
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 25);
    const summary = potreeQueueData ? ` — ${q.running} running, ${q.queued} queued` : "";
    return `
      <details id="rd-potree-queue" ${potreeQueueOpen ? "open" : ""}>
        <summary class="small" style="cursor:pointer; color:var(--muted);">Potree queue${summary}</summary>
        <div style="display:grid; gap:3px; padding:4px 0 0 2px; max-height:220px; overflow:auto;">
          ${potreeQueueData === null
            ? `<div class="small" style="color:var(--muted);">Loading…</div>`
            : (jobs.map(potreeJobRowHtml).join("") || `<div class="small" style="color:var(--muted);">No Potree jobs yet.</div>`)}
        </div>
      </details>`;
  }

  function unmappedHtml(unmapped) {
    if (!unmapped?.length) return "";
    const rows = unmapped.slice(0, 5).map((u) => `<div title="${esc(u.reason)}">&#9888; ${esc(u.dir)} <span style="color:var(--muted);">— ${esc(u.reason)}</span></div>`).join("");
    return `<div class="small" style="color:#f59e0b; margin-top:6px; display:grid; gap:2px;">${rows}${unmapped.length > 5 ? `<div>… and ${unmapped.length - 5} more unmapped folders</div>` : ""}</div>`;
  }

  function autoHtml() {
    const rule = autoRule();
    const enabled = Boolean(rule?.enabled);
    const content = autoContent();
    const potreeOn = autoPotreeEnabled();
    const opusSubmitOn = autoOpusSubmitEnabled();
    const surfaceOn = autoSurfaceEnabled();
    const surfaceRes = autoSurfaceResolutionValue();
    const surfaceBands = autoSurfaceOutputTypesValue();
    const res = rule?.last_result;
    const contentRow = `
      <div class="row" style="gap:12px; flex-wrap:wrap;">
        <label class="small" style="display:flex; gap:5px; align-items:center;"><input type="checkbox" id="rd-auto-gopro" ${content.gopro ? "checked" : ""} ${autoBusy ? "disabled" : ""}/> GoPro 360 (jpg, mp4, 360)</label>
        <label class="small" style="display:flex; gap:5px; align-items:center;"><input type="checkbox" id="rd-auto-las" ${content.las ? "checked" : ""} ${autoBusy ? "disabled" : ""}/> LAS point clouds (las, laz)</label>
        <label class="small" style="display:flex; gap:5px; align-items:center;"><input type="checkbox" id="rd-auto-trimble" ${content.trimble ? "checked" : ""} ${autoBusy ? "disabled" : ""}/> Trimble GNSS obs files (TRIMBLE/*.##O)</label>
      </div>
      <label class="small" style="display:flex; gap:5px; align-items:center; margin-left:22px;" title="${content.las ? "" : "Enable LAS point clouds above first"}">
        <input type="checkbox" id="rd-auto-potree" ${potreeOn ? "checked" : ""} ${autoBusy || !content.las ? "disabled" : ""}/>
        Auto-create a Potree project for each uploaded LAS/LAZ file (named after the file)
      </label>
      <label class="small" style="display:flex; gap:5px; align-items:center; margin-left:22px;" title="${content.trimble ? "" : "Enable Trimble GNSS obs files above first"}">
        <input type="checkbox" id="rd-auto-opus-submit" ${opusSubmitOn ? "checked" : ""} ${autoBusy || !content.trimble ? "disabled" : ""}/>
        Auto-submit each uploaded observation file to NGS OPUS (trims 2 min off both ends first, Trimble R10 defaults)
      </label>
      <label class="small" style="display:flex; gap:5px; align-items:center; margin-left:22px;" title="${content.las ? "" : "Enable LAS point clouds above first"}">
        <input type="checkbox" id="rd-auto-surface" ${surfaceOn ? "checked" : ""} ${autoBusy || !content.las ? "disabled" : ""}/>
        Auto-build a Processed Surface (raster) for each uploaded LAS/LAZ file
      </label>
      <div class="small" style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-left:44px; color:var(--muted);">
        <span>Grid res:</span>
        <input id="rd-auto-surface-res" type="number" min="0.05" max="5000" step="0.05" value="${surfaceRes === "" ? "" : esc(surfaceRes)}" placeholder="e.g. 0.5" ${autoBusy || !content.las ? "disabled" : ""} style="width:60px; font-size:10px; font-family:monospace; background:rgba(15,23,42,0.5); border:1px solid rgba(148,163,184,0.25); border-radius:3px; color:var(--text); padding:1px 4px;" />
        <span>(ft/m per cell — matches the source file's native unit; CRS auto-resolves from the project's configured horizontal CRS)</span>
      </div>
      <div class="small" style="display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-left:44px; color:var(--muted);">
        <span>Grid stats:</span>
        ${RD_SURFACE_BAND_STATS.map((stat) => `
          <label style="display:flex; align-items:center; gap:3px; cursor:pointer;">
            <input type="checkbox" class="rd-auto-surface-band" data-band="${stat.key}" ${surfaceBands.includes(stat.key) ? "checked" : ""} ${autoBusy || !content.las ? "disabled" : ""}/>${stat.label}
          </label>
        `).join("")}
      </div>`;
    let body = "";
    if (autoConfirmOpen) {
      const p = autoPreview;
      body = `
        <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,0.2); border-radius:6px; display:grid; gap:6px;">
          ${contentRow}
          ${autoPreviewLoading ? `<div class="small" style="color:var(--muted);">Computing what would sync…</div>` : ""}
          ${p?.error ? `<div class="small" style="color:#ef4444;">${esc(p.error)}</div>` : ""}
          ${p && !p.error ? `
            <div class="small">
              <b>${p.mappedFolders.length}</b> marina folder${p.mappedFolders.length === 1 ? "" : "s"} mapped to projects.
              Enabling now syncs <b>${p.pendingFiles} file${p.pendingFiles === 1 ? "" : "s"} (${fmtBytes(p.pendingBytes)})</b>,
              then new files after every scan.
            </div>
            ${unmappedHtml(p.unmapped)}
          ` : ""}
          <div class="row" style="gap:6px;">
            <button id="rd-auto-enable" class="btn" ${autoBusy || autoPreviewLoading || !!p?.error ? "disabled" : ""}>Enable Automation</button>
            <button id="rd-auto-cancel" class="btn">Cancel</button>
          </div>
        </div>`;
    } else if (enabled) {
      const lastLine = !rule.last_run_at
        ? "No passes yet — runs after each scan."
        : `Last pass ${fmtWhen(rule.last_run_at)}${res ? ` — ${res.status}: ${res.copied} copied, ${res.skipped} skipped${res.errors ? `, ${res.errors} errors` : ""}${res.mappedFolders != null ? ` · ${res.mappedFolders} folders mapped` : ""}` : ""}`;
      body = `
        <div style="margin-top:6px; display:grid; gap:6px;">
          ${contentRow}
          <div class="small" style="color:var(--muted);">${esc(lastLine)}</div>
          ${passProgressHtml()}
          ${unmappedHtml(res?.unmapped)}
          <div class="row" style="gap:6px;">
            <button id="rd-auto-run" class="btn small" ${auto?.running || autoBusy ? "disabled" : ""}>Run Now</button>
          </div>
          ${autoRunsHtml()}
        </div>`;
    }
    return `
      <details class="toolset-group" open>
        <summary>Automation</summary>
        <div class="toolset-content">
          <label class="small" style="display:flex; gap:6px; align-items:center; cursor:pointer;">
            <input type="checkbox" id="rd-auto-toggle" ${enabled || autoConfirmOpen ? "checked" : ""} ${autoBusy || auto === null ? "disabled" : ""}/>
            <span><b>360 Automation</b> — after each scan, send new files from every mapped marina folder to that project's DAM</span>
          </label>
          ${body}
          <div style="margin-top:8px;">${potreeQueueHtml()}</div>
        </div>
      </details>`;
  }

  function transferHtml() {
    if (!transfer) return "";
    const t = transfer;
    // Byte progress moves within a file; file count alone sits at 0/1 for the
    // whole of a single multi-GB LAS transfer.
    const bytesDone = (t.bytesCopied || 0) + (t.bytesInFlight || 0);
    const filePct = t.total ? (t.done / t.total) * 100 : 0;
    const bytePct = t.bytesTotal ? (bytesDone / t.bytesTotal) * 100 : 0;
    const pct = Math.min(100, Math.round(Math.max(filePct, bytePct)));
    const running = t.status === "running";
    const label = running
      ? `Sending to ${esc(t.marina)} / ${esc(t.category)} — ${t.done}/${t.total} files (${t.copied} copied, ${t.skipped} skipped) · ${esc(fmtBytes(bytesDone))} of ${esc(fmtBytes(t.bytesTotal))}`
      : `Last transfer to ${esc(t.marina)} / ${esc(t.category)}: ${esc(t.status)} — ${t.copied} copied, ${t.skipped} already in DAM${t.skippedUnstable ? `, ${t.skippedUnstable} still uploading` : ""}${t.conflictCount ? `, ${t.conflictCount} size conflicts` : ""}${t.errorCount ? `, ${t.errorCount} failed` : ""}`;
    return `
      <div style="margin-bottom:8px; padding:6px 8px; border:1px solid rgba(148,163,184,0.2); border-radius:6px;">
        <div class="small" style="display:flex; justify-content:space-between; gap:8px; align-items:center;">
          <span style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${label}</span>
          ${running ? `<button id="rd-xfer-cancel" class="btn small">Cancel</button>` : ""}
        </div>
        ${running ? `<div style="height:4px; border-radius:2px; background:rgba(148,163,184,0.2); margin-top:6px;"><div style="height:100%; width:${pct}%; border-radius:2px; background:var(--accent, #60a5fa);"></div></div>` : ""}
        ${t.errorCount ? `<div class="small" style="color:#ef4444; margin-top:4px;">${t.errors.slice(0, 3).map((e) => `${esc(e.path)}: ${esc(e.error)}`).join("<br/>")}${t.errorCount > 3 ? `<br/>… and ${t.errorCount - 3} more` : ""}</div>` : ""}
      </div>`;
  }

  function sendFormHtml() {
    const fileCount = tree.files.length + tree.dirs.reduce((a, d) => a + d.files, 0);
    const unstable = tree.files.filter((f) => f.stable === false).length + tree.dirs.reduce((a, d) => a + d.unstable, 0);
    return `
      <div style="margin-bottom:8px; padding:8px; border:1px solid rgba(148,163,184,0.2); border-radius:6px; display:grid; gap:6px;">
        <div class="small">Send <b>${esc(dir.split("/").pop())}</b> (${fileCount} file${fileCount === 1 ? "" : "s"}, subfolders included${unstable ? `; ${unstable} still uploading will be skipped` : ""}) to a project's DAM. Files already there with the same size are skipped.</div>
        <div class="row" style="gap:6px; flex-wrap:wrap;">
          <select id="rd-send-project" style="flex:2; min-width:140px;">
            <option value="">${projects === null ? "Loading projects…" : "Select project…"}</option>
            ${(projects || []).map((p) => `<option value="${esc(p.code)}" ${p.code === sendProject ? "selected" : ""}>${esc(p.name)}</option>`).join("")}
          </select>
          <select id="rd-send-category" style="flex:1; min-width:120px;">
            ${DAM_CATEGORIES.map((c) => `<option value="${esc(c)}" ${c === sendCategory ? "selected" : ""}>${esc(c)}</option>`).join("")}
          </select>
        </div>
        <div class="row" style="gap:6px;">
          <button id="rd-send-start" class="btn" ${!sendProject ? "disabled" : ""}>Start Transfer</button>
          <button id="rd-send-close" class="btn">Cancel</button>
        </div>
      </div>`;
  }

  function crumbsHtml() {
    const parts = dir ? dir.split("/") : [];
    let acc = "";
    const crumbs = [`<span data-rd-crumb="" style="cursor:pointer; color:var(--accent, #60a5fa);">Remote</span>`];
    for (const p of parts) {
      acc = acc ? `${acc}/${p}` : p;
      crumbs.push(`<span data-rd-crumb="${esc(acc)}" style="cursor:pointer; color:var(--accent, #60a5fa);">${esc(p)}</span>`);
    }
    return crumbs.join(`<span style="color:var(--muted); margin:0 2px;">/</span>`);
  }

  function dirRow(d) {
    const syncNote = d.synced > 0 ? `${d.synced}/${d.files} synced` : `${d.files} file${d.files === 1 ? "" : "s"}`;
    const flags = [
      d.unstable > 0 ? `<span class="small" style="color:#f59e0b;">${d.unstable} uploading</span>` : "",
    ].filter(Boolean).join(" ");
    return `
      <div data-rd-dir="${esc(d.name)}" style="display:flex; justify-content:space-between; align-items:center; gap:8px; padding:3px 8px; border:1px solid rgba(148,163,184,0.14); border-radius:6px; cursor:pointer;">
        <span class="small" style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">&#128193; ${esc(d.name)}</span>
        <span class="small" style="color:var(--muted); white-space:nowrap;">${esc(syncNote)} &middot; ${esc(fmtBytes(d.bytes))} ${flags}</span>
      </div>`;
  }

  function fileRow(f, pathLabel = null) {
    return `
      <div style="display:flex; justify-content:space-between; align-items:center; gap:8px; padding:3px 8px; border:1px solid rgba(148,163,184,0.14); border-radius:6px;">
        <span class="small" style="min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${esc(pathLabel || f.name)}">${esc(pathLabel || f.name)}</span>
        <span class="small" style="color:var(--muted); white-space:nowrap;">${esc(fmtBytes(f.sizeBytes ?? f.size_bytes))} ${badgeHtml(fileBadge(f))}</span>
      </div>`;
  }

  function render() {
    if (!containerEl) return;
    const t = status?.totals;
    const last = status?.lastScan;
    const configured = !!status?.configured;
    const lastLine = !last
      ? "Never scanned."
      : last.status === "running"
        ? "Scan in progress…"
        : `Last scan ${fmtWhen(last.finished_at)} — ${last.status === "ok" ? `${last.files_seen} files, ${fmtBytes(last.bytes_seen)}${last.new_files ? `, ${last.new_files} new` : ""}${last.missing_files ? `, ${last.missing_files} removed` : ""}` : `failed: ${last.error || "unknown error"}`}`;

    const searchResultsHtml = results
      ? (results.files?.length
          ? results.files.map((f) => fileRow({ ...f, stable: f.stable, syncState: f.sync_state }, f.remote_path)).join("")
          : `<div class="small" style="color:var(--muted); padding:6px;">No matches.</div>`)
      : "";

    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted); padding-bottom:8px;">
        Field data on the CloudShare account, inventoried by the HydroTools server.
        Status shows whether each remote file also exists in local storage.
      </div>

      <div class="subtoolset-list" style="--subtoolset-bleed-top:0px;">
        <details class="toolset-group" open>
          <summary>Connection</summary>
          <div class="toolset-content">
            ${statusError ? `<div class="small" style="color:#ef4444;">${esc(statusError)}</div>` : ""}
            ${!status ? `<div class="small" style="color:var(--muted);">Loading…</div>` : `
              <div class="small" style="display:grid; grid-template-columns:auto 1fr; gap:2px 10px;">
                <span style="color:var(--muted);">Host</span><span>${esc(status.connection?.host)}${status.connection?.secure ? " (FTPS)" : ""}</span>
                <span style="color:var(--muted);">Root</span><span>${esc(status.connection?.root || "/")}</span>
                <span style="color:var(--muted);">Local root</span><span>${esc(status.localRoot || "not configured")}</span>
                <span style="color:var(--muted);">Manifest</span><span>${t ? `${t.files} files · ${fmtBytes(t.bytes)} · ${t.synced} synced${t.unstable ? ` · ${t.unstable} uploading` : ""}` : "—"}</span>
              </div>
              <div class="small" style="color:var(--muted); margin-top:6px;">${esc(lastLine)}</div>
              ${!configured ? `<div class="small" style="color:#f59e0b; margin-top:6px;">Connector not configured — set CLOUDSHARE_FTP_USER / CLOUDSHARE_FTP_PASSWORD on the server.</div>` : ""}
              <div class="row" style="margin-top:8px; gap:6px;">
                <button id="rd-scan" class="btn" ${!configured || status.scanning ? "disabled" : ""}>${status.scanning ? "Scanning…" : "Scan Now"}</button>
                <button id="rd-refresh" class="btn">Refresh</button>
              </div>
            `}
          </div>
        </details>

        ${autoHtml()}

        ${mirrorHtml()}

        <details class="toolset-group" open>
          <summary>Remote Files</summary>
          <div class="toolset-content">
            <div class="row" style="margin-bottom:8px;">
              <input id="rd-search" type="text" placeholder="Search all remote files…" value="${esc(query)}" style="flex:1;" />
            </div>
            ${results ? `
              <div class="small" style="color:var(--muted); margin-bottom:6px;">${results.total} match${results.total === 1 ? "" : "es"}${results.total > (results.files?.length || 0) ? ` (showing first ${results.files.length})` : ""}</div>
              <div style="display:grid; gap:2px; max-height:420px; overflow:auto;">${searchResultsHtml}</div>
            ` : `
              <div class="row" style="margin-bottom:6px; justify-content:space-between; align-items:center; gap:8px;">
                <div class="small" style="min-width:0;">${crumbsHtml()}</div>
                ${dir && (tree.files.length || tree.dirs.length) ? `<button id="rd-send-open" class="btn small" ${transfer?.status === "running" ? "disabled" : ""}>Send to DAM…</button>` : ""}
              </div>
              ${transferHtml()}
              ${sendOpen ? sendFormHtml() : ""}
              ${treeLoading ? `<div class="small" style="color:var(--muted);">Loading…</div>` : `
                <div style="display:grid; gap:2px; max-height:420px; overflow:auto;">
                  ${tree.dirs.map(dirRow).join("")}
                  ${tree.files.map((f) => fileRow(f)).join("")}
                  ${!tree.dirs.length && !tree.files.length ? `<div class="small" style="color:var(--muted); padding:6px;">Nothing here yet${configured ? " — run a scan to inventory the account." : "."}</div>` : ""}
                </div>
              `}
            `}
          </div>
        </details>
      </div>
    `;

    containerEl.querySelector("#rd-auto-toggle")?.addEventListener("change", (e) => {
      if (e.target.checked) openAutoConfirm();
      else if (autoConfirmOpen) { autoConfirmOpen = false; autoPreview = null; render(); }
      else saveAutoRule({ enabled: false });
    });
    containerEl.querySelector("#rd-auto-enable")?.addEventListener("click", () => saveAutoRule({ enabled: true }));
    containerEl.querySelector("#rd-auto-cancel")?.addEventListener("click", () => { autoConfirmOpen = false; autoPreview = null; render(); });
    containerEl.querySelector("#rd-auto-run")?.addEventListener("click", runAutoNow);
    containerEl.querySelector("#rd-auto-runs")?.addEventListener("toggle", (e) => {
      autoRunsOpen = e.target.open;
      if (autoRunsOpen && autoRuns === null) loadAutoRuns();
    });
    for (const key of ["gopro", "las", "trimble"]) {
      containerEl.querySelector(`#rd-auto-${key}`)?.addEventListener("change", (e) => {
        const content = { ...autoContent(), [key]: e.target.checked };
        if (autoConfirmOpen) {
          // Not saved yet — recompute the preview with the new content mix.
          const rule = autoRule();
          if (rule) rule.config = { ...(rule.config || {}), content };
          else auto.rules = [{ scope: "client", client_id: "safeharbor", config: { content } }];
          openAutoConfirm();
        } else {
          saveAutoRule({ content });
        }
      });
    }
    containerEl.querySelector("#rd-auto-potree")?.addEventListener("change", (e) => {
      const autoPotree = e.target.checked;
      if (autoConfirmOpen) {
        // Doesn't affect the sync preview — just stash it on the draft rule until saved.
        const rule = autoRule();
        if (rule) rule.config = { ...(rule.config || {}), autoPotree };
        else auto.rules = [{ scope: "client", client_id: "safeharbor", config: { content: autoContent(), autoPotree } }];
        render();
      } else {
        saveAutoRule({ autoPotree });
      }
    });
    containerEl.querySelector("#rd-auto-opus-submit")?.addEventListener("change", (e) => {
      const autoOpusSubmit = e.target.checked;
      if (autoConfirmOpen) {
        // Doesn't affect the sync preview — just stash it on the draft rule until saved.
        const rule = autoRule();
        if (rule) rule.config = { ...(rule.config || {}), autoOpusSubmit };
        else auto.rules = [{ scope: "client", client_id: "safeharbor", config: { content: autoContent(), autoOpusSubmit } }];
        render();
      } else {
        saveAutoRule({ autoOpusSubmit });
      }
    });
    containerEl.querySelector("#rd-auto-surface")?.addEventListener("change", (e) => {
      const autoSurface = e.target.checked;
      if (autoSurface && !autoSurfaceResolutionValue()) {
        app.ui.toast("Set a grid resolution first.");
        e.target.checked = false;
        return;
      }
      if (autoConfirmOpen) {
        const rule = autoRule();
        if (rule) rule.config = { ...(rule.config || {}), autoSurface };
        else auto.rules = [{ scope: "client", client_id: "safeharbor", config: { content: autoContent(), autoSurface } }];
        render();
      } else {
        saveAutoRule({ autoSurface });
      }
    });
    containerEl.querySelector("#rd-auto-surface-res")?.addEventListener("change", (e) => {
      const autoSurfaceResolution = Number(e.target.value) || null;
      if (autoConfirmOpen) {
        const rule = autoRule();
        if (rule) rule.config = { ...(rule.config || {}), autoSurfaceResolution };
        else auto.rules = [{ scope: "client", client_id: "safeharbor", config: { content: autoContent(), autoSurfaceResolution } }];
        render();
      } else {
        saveAutoRule({ autoSurfaceResolution });
      }
    });
    containerEl.querySelectorAll(".rd-auto-surface-band").forEach((cb) => {
      cb.addEventListener("change", () => {
        const autoSurfaceOutputTypes = [...containerEl.querySelectorAll(".rd-auto-surface-band:checked")].map((el) => el.dataset.band);
        if (autoConfirmOpen) {
          const rule = autoRule();
          if (rule) rule.config = { ...(rule.config || {}), autoSurfaceOutputTypes };
          else auto.rules = [{ scope: "client", client_id: "safeharbor", config: { content: autoContent(), autoSurfaceOutputTypes } }];
          render();
        } else {
          saveAutoRule({ autoSurfaceOutputTypes });
        }
      });
    });
    containerEl.querySelector("#rd-potree-queue")?.addEventListener("toggle", (e) => {
      potreeQueueOpen = e.target.open;
      if (potreeQueueOpen) {
        if (potreeQueueData === null) loadPotreeQueue();
        if (!potreeQueuePollTimer) potreeQueuePollTimer = setInterval(loadPotreeQueue, 5000);
      } else if (potreeQueuePollTimer) {
        clearInterval(potreeQueuePollTimer);
        potreeQueuePollTimer = null;
      }
    });
    containerEl.querySelector("#rd-mirror-toggle")?.addEventListener("change", (e) => {
      if (e.target.checked) openMirrorConfirm();
      else if (mirrorConfirmOpen) { mirrorConfirmOpen = false; mirrorPreview = null; render(); }
      else setMirrorEnabled(false);
    });
    containerEl.querySelector("#rd-mirror-enable")?.addEventListener("click", () => setMirrorEnabled(true));
    containerEl.querySelector("#rd-mirror-cancel")?.addEventListener("click", () => { mirrorConfirmOpen = false; mirrorPreview = null; render(); });
    containerEl.querySelector("#rd-mirror-run")?.addEventListener("click", runMirrorNow);
    containerEl.querySelector("#rd-scan")?.addEventListener("click", startScan);
    containerEl.querySelector("#rd-refresh")?.addEventListener("click", () => { loadStatus(); loadTree(); });
    containerEl.querySelector("#rd-send-open")?.addEventListener("click", () => {
      sendOpen = !sendOpen;
      if (sendOpen && projects === null) loadProjects();
      render();
    });
    containerEl.querySelector("#rd-send-close")?.addEventListener("click", () => { sendOpen = false; render(); });
    containerEl.querySelector("#rd-send-start")?.addEventListener("click", startTransfer);
    containerEl.querySelector("#rd-xfer-cancel")?.addEventListener("click", cancelTransfer);
    containerEl.querySelector("#rd-send-project")?.addEventListener("change", (e) => { sendProject = e.target.value; render(); });
    containerEl.querySelector("#rd-send-category")?.addEventListener("change", (e) => { sendCategory = e.target.value; });
    const search = containerEl.querySelector("#rd-search");
    search?.addEventListener("input", () => {
      query = search.value;
      clearTimeout(searchTimer);
      searchTimer = setTimeout(runSearch, 300);
    });
    containerEl.querySelectorAll("[data-rd-dir]").forEach((el) => {
      el.addEventListener("click", () => openDir(el.getAttribute("data-rd-dir")));
    });
    containerEl.querySelectorAll("[data-rd-crumb]").forEach((el) => {
      el.addEventListener("click", () => { dir = el.getAttribute("data-rd-crumb") || ""; loadTree(); });
    });
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    render();
    loadStatus();
    loadTree();
    loadAutomation();
    loadMirror();
    pollTransfer(); // pick up a transfer already running (e.g. tool re-opened mid-transfer)
  }

  function destroy() {
    clearTimeout(searchTimer);
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (transferTimer) { clearInterval(transferTimer); transferTimer = null; }
    if (autoPollTimer) { clearInterval(autoPollTimer); autoPollTimer = null; }
    if (potreeQueuePollTimer) { clearInterval(potreeQueuePollTimer); potreeQueuePollTimer = null; }
    if (mirrorPollTimer) { clearInterval(mirrorPollTimer); mirrorPollTimer = null; }
    containerEl = null;
  }

  return { mount, destroy };
}

export const remoteDataTool = {
  id: "remote-data",
  name: "Remote Data",
  icon: "&#9729;",
  init(app) {
    this._rt = this._rt ?? createRemoteDataTool(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRemoteDataTool(app);
    this._rt.mount(containerEl);
  },
};
